import { createNavigation } from '../components/navigation.js';
import { createFooter } from '../components/footer.js';

export function createHomePage(): string {
  return `
    ${createNavigation()}

    <!-- Hero Section -->
    <section id="home" class="hero-section">
        <div class="hero-content">
            <h1 class="hero-title">Meet <span class="accent">creators</span> irl.</h1>
            <h2 class="hero-subtitle">Grab a coffee. Walk and talk. It's all about the <span class="accent">vibe</span>.</h2>
            <p class="hero-description">
                Meet <em>VibeChk</em>, the app for GenZ to find founders, engineers, designers, artists, content creators, growth hackers, and VCs/angels irl. Ditch cold emails or LinkedIn DMs—we prioritize in-person experience and see if you <span class="accent">vibe</span> with the other.
            </p>
            
            <!-- VibeChk Trailer Video -->
            <div class="video-container" id="hero-video">
                <iframe 
                    src="https://www.youtube.com/embed/2xVtcuz__n4?si=Eh_U7viERjaMY0T3" 
                    frameborder="0" 
                    allowfullscreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    title="VibeChk Trailer">
                </iframe>
            </div>
            
            <div class="hero-buttons">
                <a href="/about" class="learn-more-button">
                    <span>Learn More</span>
                    <svg class="learn-more-icon" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
                    </svg>
                </a>
                <!-- <button onclick="openSignupModal()" class="hero-app-store-btn"> -->
                <a href="https://apps.apple.com/us/app/vibechk/id6748249688" target="_blank" rel="noopener noreferrer" class="hero-app-store-btn">
                    <svg class="app-store-icon" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div class="app-store-text">
                        <span class="app-store-small">Get it on</span>
                        <span class="app-store-large">App Store</span>
                    </div>
                </a>
                <!-- </button> -->
            </div>
        </div>
    </section>

    <!-- How It Works Section -->
    <section class="how-it-works-section">
        <div class="container">
            <h2 class="section-title">How It Works</h2>
            
            <div class="step-switcher">
                <button class="step-btn active" data-step="1">01</button>
                <button class="step-btn" data-step="2">02</button>
                <button class="step-btn" data-step="3">03</button>
                <button class="step-btn" data-step="4">04</button>
                <button class="step-btn" data-step="5">05</button>
            </div>
            
            <div class="interactive-step-container">
                <div class="phone-mockup-large">
                </div>
                
                <div class="step-content-large">
                    <div class="step-item active" data-step="1">
                        <h3>Build Your <span class="accent">Profile</span></h3>
                        <p>Create your identity that shows your roles, where you’re based, and what exciting things you're working on.</p>
                    </div>
                    
                    <div class="step-item" data-step="2">
                        <h3>Find <span class="accent">Creators</span> on the Map</h3>
                        <p>Discover all types of creators in your area through our interactive global creator map.</p>
                    </div>
                    
                    <div class="step-item" data-step="3">
                        <h3>Select the Person to <span class="accent">Vibe</span> With</h3>
                        <p>Choose who you like to meetup irl, including some famous GenZ founders, VCs, and cracked engineers.</p>
                    </div>
                    
                    <div class="step-item" data-step="4">
                        <h3><em><span class="accent">Vibe IRL: </span></em>Coffee Chat or Walk & Talk</h3>
                        <p>Send invite for in-person coffee chats or casual walks to see if you truly <span class="accent">vibe</span> with the person.</p>
                    </div>

                    <div class="step-item" data-step="5">
                        <h3><span class="accent">Manage</span> your calendar to see your bookings</h3>
                        <p>Manage invites, past bookings, and other creators you followed, build genuine connections irl.</p>
                    </div>
                </div>
            </div>
        </div>
    </section>
    

    <!-- Why Choose VibeChk Section -->
    <section class="why-choose-section">
        <div class="container">
            <h2 class="section-title">Why choose <em>VibeChk</em>?</h2>
            <div class="benefits-grid">
                <div class="benefit-box">
                    <div class="benefit-icon">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="currentColor"/>
                        </svg>
                    </div>
                    <h3>Build genuine connections irl</h3>
                    <p>Skip endless cold Email/DMs by meeting <span class="accent">creators</span> irl for genuine vibes and experiences.</p>
                </div>
                <div class="benefit-box">
                    <div class="benefit-icon">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C15.866 2 19 5.13401 19 9C19 14.25 12 22 12 22C12 22 5 14.25 5 9C5 5.13401 8.13401 2 12 2ZM12 6C10.3431 6 9 7.34315 9 9C9 10.6569 10.3431 12 12 12C13.6569 12 15 10.6569 15 9C15 7.34315 13.6569 6 12 6Z" fill="currentColor"/>
                        </svg>
                    </div>
                    <h3>Discover creators everywhere</h3>
                    <p>Find <span class="accent">creators</span> near you, and potentially meet celebrity founders of the world.</p>
                </div>
                <div class="benefit-box">
                    <div class="benefit-icon">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16 6L18.29 8.29L13.41 13.17L9.41 9.17L2 16.59L3.41 18L9.41 12L13.41 16L19.71 9.71L22 12V6H16Z" fill="currentColor"/>
                        </svg>
                    </div>
                    <h3>Increase your network and opportunities<span class="accent-black"></span></h3>
                    <p>In-person meetups builds your friendship, partnership, and even <span class="accent">relationship</span>.</p>
                </div>
            </div>
        </div>
    </section>  

    <!-- App Store CTA Section -->
    <section id="app-store" class="app-store-section">
        <div class="container">
            <h2 class="app-store-title">
                <span class="app-store-get">Get</span>
                <img src="./assets/logo_shadow.png" alt="VibeChk Logo" class="app-store-logo" />
                <span class="app-store-vibechk"><em>VibeChk</em></span>
            </h2>
            <p>Start meeting creators in your area today</p>
            <!-- <button onclick="openSignupModal()" class="app-store-cta-btn"> -->
            <a href="https://apps.apple.com/us/app/vibechk/id6748249688" target="_blank" rel="noopener noreferrer" class="app-store-cta-btn">
                <svg class="app-store-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div class="app-store-text">
                    <span class="app-store-small">Get it on</span>
                        <span class="app-store-large">App Store</span>
                </div>
            </a>
            <!-- </button> -->
        </div>
    </section> 

    ${createFooter()}
  `;
} 